import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import FilterBuilder from 'devextreme-react/filter-builder';
import TextBox from 'devextreme-react/text-box';
import Button from 'devextreme-react/button';
import Toolbar, { Item as ToolbarItem } from 'devextreme-react/toolbar';
import '../../pages/creazioneFiltri/CreazioneFiltri.css';

interface FiltroProps {
  fields: any[];
  filterValue: any;
  filterName: string;
  onFilterValueChanged: (value: any) => void;
  onFilterNameChanged: (value: string) => void;
  onResetFilter: () => void;
  onShowPreview: () => void;
  onSaveFilter: () => Promise<boolean>;
  isSaving?: boolean;
}

// Definizione manuale del tipo di campo per FilterBuilder basato sulla documentazione DevExtreme
interface FilterField {
  dataField: string;
  dataType: "string" | "number" | "date" | "boolean" | "object" | undefined;
  caption?: string;
  lookup?: {
    dataSource: any;
    valueExpr: string;
    displayExpr: string;
  };
}

// Mappa dei tipi per convertire i tipi dei campi nel formato corretto per FilterBuilder
const dataTypeMap: Record<string, "string" | "number" | "date" | "boolean" | "object" | undefined> = {
  string: "string",
  text: "string",
  number: "number",
  int: "number",
  integer: "number",
  float: "number",
  double: "number",
  decimal: "number",
  date: "date",
  datetime: "date",
  time: "date",
  boolean: "boolean",
  bool: "boolean",
  object: "object"
};

const Filtro: React.FC<FiltroProps> = ({
  fields,
  filterValue,
  filterName,
  onFilterValueChanged,
  onFilterNameChanged,
  onResetFilter,
  onShowPreview,
  onSaveFilter,
  isSaving = false
}) => {
  // Stato locale per controllare il valore del filtro
  // Questo aiuta a gestire il reset in modo sicuro
  const [localFilterValue, setLocalFilterValue] = useState<any>(filterValue);
  
  // Flag per monitorare quando stiamo resettando manualmente
  const isResetting = useRef(false);
  
  // Sincronizza lo stato locale con le props esterne quando non stiamo resettando
  useEffect(() => {
    if (!isResetting.current) {
      setLocalFilterValue(filterValue);
    }
  }, [filterValue]);
  
  // Riferimento al FilterBuilder
  const filterBuilderRef = useRef<any>(null);
  
  // Verifica se il nome è valido (non vuoto e non solo spazi)
  const isValidName = filterName && filterName.trim().length >= 3;
  
  // Converte i campi nel formato richiesto da FilterBuilder
  const formattedFields = useMemo<FilterField[]>(() => {
    if (!fields || fields.length === 0) return [];
    
    return fields.map(field => {
      // Il formato dei campi ricevuti potrebbe essere diverso, adattiamo i nomi
      const dataField = field.dataField || field.name;
      const rawType = (field.dataType || field.type || 'string').toLowerCase();
      
      // Mappa il tipo al formato corretto per FilterBuilder
      const dataType = dataTypeMap[rawType] || "string";
      
      // Assicuriamoci che il campo abbia un dataField valido
      if (!dataField) {
        console.warn('Campo senza dataField rilevato:', field);
        return null;
      }
      
      return {
        dataField,
        dataType,
        caption: field.caption || dataField
      };
    }).filter(Boolean) as FilterField[]; 
  }, [fields]);
  
  // Verifica se ci sono filtri definiti e se sono validi
  const hasFilterValue = useMemo(() => {
    const valueToCheck = localFilterValue;
    
    // console.log('Verifica hasFilterValue. filterValue:', valueToCheck);
    
    if (!valueToCheck) return false;
    
    if (Array.isArray(valueToCheck)) {
      if (valueToCheck.length === 0) return false;
      
      if (valueToCheck.length >= 2) {
        return true;
      } else if (valueToCheck.length === 1) {
        const firstItem = valueToCheck[0];
        return typeof firstItem === 'string' && ['and', 'or', 'not'].includes(firstItem.toLowerCase());
      }
    }
    
    return false; // In caso di dubbio, consideriamo il filtro non valido
  }, [localFilterValue]);

  // Pulisce il valore del filtro per assicurarsi che usi solo campi validi
  const cleanFilterValue = useMemo(() => {
    // Usiamo lo stato locale per processare il valore
    const valueToClean = localFilterValue;
    
    if (!valueToClean || !Array.isArray(valueToClean) || valueToClean.length === 0) {
      return []; // Restituisce un array vuoto se non c'è un valore di filtro
    }
    
    if (formattedFields.length === 0) {
      return []; // Non possiamo avere un filtro valido senza campi
    }
    
    // Estrae tutti i nomi dei campi disponibili per la verifica
    const availableFields = formattedFields.map(f => f.dataField);
    
    // Funzione ricorsiva per pulire il filtro
    const cleanFilter = (item: any): any => {
      if (!item) return null;
      
      if (Array.isArray(item)) {
        // Se è un operatore (and, or, not)
        if (typeof item[0] === 'string' && ['and', 'or', 'not'].includes(item[0].toLowerCase())) {
          const operator = item[0];
          const subConditions = item.slice(1).map(cleanFilter).filter(Boolean);
          
          // Se non ci sono sotto-condizioni valide, restituisci null
          if (subConditions.length === 0) return null;
          
          return [operator, ...subConditions];
        }
        
        // Se è una condizione semplice [campo, operatore, valore]
        if (item.length >= 2) {
          const field = item[0];
          
          // Se il campo non esiste, salta questa condizione
          if (!field || !availableFields.includes(field)) {
            console.warn(`Campo ${field} non trovato nei campi disponibili`);
            return null;
          }
          
          return item;
        }
      }
      
      return null;
    };
    
    const cleanedValue = cleanFilter(valueToClean);
    
    // Se il risultato della pulizia è null o un array vuoto, restituisce un array vuoto
    if (!cleanedValue || (Array.isArray(cleanedValue) && cleanedValue.length === 0)) {
      return [];
    }
    
    return cleanedValue;
  }, [localFilterValue, formattedFields]);

  // Gestisci il cambiamento del valore del filtro in modo memoizzato
  const handleFilterValueChanged = useCallback((e: any) => {
    if (!e) {
      console.warn('Evento onValueChanged del FilterBuilder nullo o non definito');
      return;
    }
    
    // Se stiamo resettando, ignora questo evento
    if (isResetting.current) {
      // console.log('Ignorando onValueChanged durante il reset');
      return;
    }
    
    // console.log('FilterBuilder onValueChanged event:', e);
    
    // Assicuriamoci di avere un valore valido
    const newValue = e.value || [];
    // console.log('Nuovo valore del filtro:', newValue);
    
    // Aggiorniamo lo stato locale e notifichiamo il componente padre
    setLocalFilterValue(newValue);
    onFilterValueChanged(newValue);
  }, [onFilterValueChanged]);

  // Gestisci il reset del filtro secondo la soluzione del ticket di supporto DevExpress
  const handleResetFilter = useCallback(() => {
    
    
    // Imposta il flag che indica che stiamo facendo un reset
    isResetting.current = true;
    
    try {
      // 1. Prima imposta lo stato locale a un array vuoto
      setLocalFilterValue([]);
      // 2. Poi chiama la funzione di reset del componente padre
      onResetFilter();
    } catch (error) {
      console.error('Errore durante il reset del FilterBuilder:', error);
    } finally {
      // Dopo un breve ritardo, resetta il flag per consentire aggiornamenti futuri
      setTimeout(() => {
        isResetting.current = false;
      }, 100);
    }
  }, [onResetFilter]);

  // Log lo stato del pulsante "Salva Filtro"
  // useEffect(() => {
  //   console.log('Stato pulsante "Salva Filtro":', {
  //     isValidName,
  //     hasFilterValue,
  //     isSaving,
  //     disabled: !isValidName || !hasFilterValue || isSaving
  //   });
  // }, [isValidName, hasFilterValue, isSaving]);

  // Non renderizzare il FilterBuilder se non ci sono campi validi
  if (formattedFields.length === 0) {
    return (
      <div>
        <div className="filter-builder-intro-text">
          <p>Caricamento dei campi in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="filter-builder-intro-text">
        <p>Definisci i criteri e assegna un nome al filtro.</p>
      </div>

      <div className="filter-builder-outer-container">
        <FilterBuilder
          ref={filterBuilderRef}
          fields={formattedFields}
          value={localFilterValue} // Usiamo lo stato locale anziché le props
          onValueChanged={handleFilterValueChanged}
          className="custom-inner-style-filterbuilder"
          width="100%"
          allowHierarchicalFields={false}
        />
      </div>

      <Toolbar className="custom-action-toolbar">
        <ToolbarItem location="before">
          <TextBox
            width={200}
            placeholder="Nome filtro (min 3 caratteri)"
            value={filterName}
            onValueChanged={(e) => onFilterNameChanged(e.value)}
            disabled={isSaving}
          />
        </ToolbarItem>
        <ToolbarItem location="before">
          <Button
            text="Salva Filtro"
            type="success"
            icon="save"
            hint="Salva il filtro corrente (nuovo o modificato) sul server."
            disabled={!isValidName || !hasFilterValue || isSaving}
            onClick={async () => {
              const success = await onSaveFilter();
              if (success) {
                // Il salvataggio è andato a buon fine, mostriamo un messaggio o facciamo altro se necessario
                // Per ora non facciamo nulla poiché il messaggio viene gestito nel componente padre
              }
            }}
          />
        </ToolbarItem>
        <ToolbarItem location="before">
          <Button
            hint="Visualizza Anteprima"
            text="Anteprima"
            icon="find"
            type="normal"
            disabled={!hasFilterValue || isSaving}
            onClick={onShowPreview}
          />
        </ToolbarItem>
        <ToolbarItem location="before">
          <Button
            text="Reset"
            type="normal"
            hint="Reset Filtro"
            icon="revert"
            disabled={isSaving}
            onClick={handleResetFilter}
          />
        </ToolbarItem>
      </Toolbar>
    </div>
  );
};

export default React.memo(Filtro);

